// V2 preview synchronization marker.
const GCC_COUNTRIES = new Set(["SA", "AE", "BH", "KW", "OM", "QA"]);

function normalizedList(value) {
  return (value || "")
    .split(",")
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
}

function parseScheduledDate(date, time, offset) {
  if (!date) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return Number.NaN;

  const normalizedTime = time || "00:00";
  if (!/^\d{2}:\d{2}$/.test(normalizedTime)) return Number.NaN;

  const timestamp = Date.parse(`${date}T${normalizedTime}:00${offset || "+03:00"}`);
  return Number.isFinite(timestamp) ? timestamp : Number.NaN;
}

function resolveCampaignState(data, context, now = Date.now()) {
  if (data.enabled !== "true") return "disabled";

  const audience = data.audience || "all";
  const country = (context.country || "").toUpperCase();
  const currency = (context.currency || "").toUpperCase();
  const customCountries = normalizedList(data.countryCodes);
  const isGcc = GCC_COUNTRIES.has(country);
  const audienceMatches =
    audience === "all" ||
    (audience === "sa" && country === "SA") ||
    (audience === "gcc" && isGcc) ||
    (audience === "international" && !isGcc) ||
    (audience === "custom" && customCountries.includes(country));

  if (!audienceMatches) return "wrong_audience";

  const requiredCurrency = (data.requiredCurrency || "").toUpperCase();
  if (requiredCurrency && requiredCurrency !== currency) return "wrong_currency";
  if (data.priceValid === "false") return "invalid_price";

  const pageScope = data.pageScope || "all";
  const customPages = normalizedList(data.pageTypes).map((item) => item.toLowerCase());
  if (pageScope !== "all" && pageScope !== "custom" && pageScope !== context.pageType) return "wrong_page";
  if (pageScope === "custom" && !customPages.includes((context.pageType || "").toLowerCase())) return "wrong_page";

  const deviceScope = data.deviceScope || "all";
  if (deviceScope === "mobile" && !context.isMobile) return "wrong_device";
  if (deviceScope === "desktop" && context.isMobile) return "wrong_device";

  if (data.scheduleEnabled !== "true") return "active";

  const start = parseScheduledDate(data.startDate, data.startTime, data.timezone);
  const end = parseScheduledDate(data.endDate, data.endTime, data.timezone);

  if (!Number.isFinite(end) || (start !== null && !Number.isFinite(start))) return "invalid_schedule";
  if (start !== null && end <= start) return "invalid_schedule";
  if (start !== null && now < start) return "before";
  if (now >= end) return "expired";
  return "active";
}

function safeStorageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (_error) {
    return null;
  }
}

function safeStorageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (_error) {
    // Storage can be unavailable in private browsing. Dismissal still works for the page view.
  }
}

function emitBarEvent(name, detail) {
  document.dispatchEvent(
    new CustomEvent("afghani-oil:bar-event", {
      detail: { event: name, ...detail },
    }),
  );
}

function focusAndScroll(selector) {
  if (!selector) return false;

  let target;
  try {
    target = document.querySelector(selector);
  } catch (_error) {
    return false;
  }

  if (!target) return false;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });

  if (!target.hasAttribute("tabindex")) {
    target.setAttribute("tabindex", "-1");
    target.dataset.temporaryBarTabindex = "true";
  }

  target.focus({ preventScroll: true });
  return true;
}

function bindScrollActions(root, signal, eventDetail) {
  root.querySelectorAll("[data-bar-scroll-target]").forEach((link) => {
    link.addEventListener(
      "click",
      (event) => {
        const didScroll = focusAndScroll(link.dataset.barScrollTarget);
        emitBarEvent("bar_click", {
          ...eventDetail(),
          destination_type: didScroll ? "scroll" : "fallback_link",
        });

        if (didScroll) event.preventDefault();
      },
      { signal },
    );
  });
}

const HTMLElementBase = globalThis.HTMLElement || class {};

class AfghaniFomoBar extends HTMLElementBase {
  connectedCallback() {
    this.abortController = new AbortController();
    this.intervalId = null;
    this.activeCampaign = null;
    this.impressedCampaigns = new Set();
    this.section = this.closest(".shopify-section");
    this.storageKey = `afghani-fomo-dismissed:${this.dataset.sectionId}`;
    this.country = (this.dataset.country || "").toUpperCase();
    this.currency = (this.dataset.currency || "").toUpperCase();
    this.pageType = this.dataset.pageType || "other";

    this.querySelector("[data-fomo-dismiss]")?.addEventListener(
      "click",
      () => this.dismiss(),
      { signal: this.abortController.signal },
    );

    this.addEventListener(
      "click",
      (event) => {
        const action = event.target.closest("[data-fomo-action]");
        if (!action || !this.activeCampaign) return;

        const selector = action.dataset.barScrollTarget;
        const didScroll = selector ? focusAndScroll(selector) : false;
        emitBarEvent("bar_click", {
          ...this.eventDetail(this.activeCampaign),
          destination_type: didScroll ? "scroll" : action.dataset.destinationType || "link",
        });

        if (didScroll) event.preventDefault();
      },
      { signal: this.abortController.signal },
    );

    document.addEventListener("visibilitychange", () => this.handleVisibility(), {
      signal: this.abortController.signal,
    });
    window.addEventListener("resize", () => this.evaluate(), {
      signal: this.abortController.signal,
    });

    if (window.Shopify?.designMode) {
      document.addEventListener(
        "shopify:block:select",
        (event) => {
          const campaign = event.target.closest?.("[data-fomo-campaign]");
          if (campaign && this.contains(campaign)) this.showCampaign(campaign, true);
        },
        { signal: this.abortController.signal },
      );
      document.addEventListener("shopify:block:deselect", () => this.evaluate(), {
        signal: this.abortController.signal,
      });
    }

    this.evaluate();
  }

  disconnectedCallback() {
    this.stopTimer();
    this.abortController?.abort();
  }

  isDismissed() {
    if (window.Shopify?.designMode || this.dataset.dismissible !== "true") return false;
    const dismissedUntil = Number(safeStorageGet(this.storageKey));
    return Number.isFinite(dismissedUntil) && dismissedUntil > Date.now();
  }

  dismiss() {
    const hours = Number(this.dataset.dismissHours || 24);
    safeStorageSet(this.storageKey, String(Date.now() + hours * 60 * 60 * 1000));
    emitBarEvent("bar_dismiss", this.eventDetail(this.activeCampaign));
    this.hideAll();
  }

  campaignState(campaign, now = Date.now()) {
    return resolveCampaignState(
      campaign.dataset,
      {
        country: this.country,
        currency: this.currency,
        pageType: this.pageType,
        isMobile: window.matchMedia("(max-width: 699px)").matches,
      },
      now,
    );
  }

  evaluate() {
    this.stopTimer();

    if (this.isDismissed()) {
      this.hideAll();
      return;
    }

    const campaigns = [...this.querySelectorAll("[data-fomo-campaign]")];
    const active = campaigns
      .filter((campaign) => this.campaignState(campaign) === "active")
      .sort((left, right) => Number(right.dataset.priority || 0) - Number(left.dataset.priority || 0))[0];

    if (!active) {
      this.hideAll();
      return;
    }

    this.showCampaign(active, false);
  }

  showCampaign(campaign, editorPreview) {
    this.stopTimer();
    this.querySelectorAll("[data-fomo-campaign]").forEach((item) => {
      item.hidden = item !== campaign;
    });

    const state = this.campaignState(campaign);
    campaign.dataset.state = editorPreview ? state : "active";
    this.activeCampaign = campaign;
    this.hidden = false;
    this.section?.setAttribute("data-fomo-active", "true");

    const status = campaign.querySelector("[data-fomo-status]");
    const action = campaign.querySelector("[data-fomo-action]");
    const countdown = campaign.querySelector("[data-fomo-countdown]");
    const liveState = editorPreview ? state : "active";

    if (status) {
      status.textContent =
        liveState === "before"
          ? campaign.dataset.beforeText || "قريباً"
          : liveState === "expired"
            ? campaign.dataset.expiredText || "انتهى العرض"
            : campaign.dataset.activeText || "العرض متاح الآن";
    }

    const diagnostic = campaign.querySelector("[data-fomo-diagnostic]");
    if (diagnostic) {
      const labels = {
        active: "جاهزة ونشطة في هذا السياق",
        before: "مجدولة ولم تبدأ بعد",
        expired: "منتهية ولن تظهر للعملاء",
        disabled: "الحملة متوقفة",
        wrong_audience: "الدولة الحالية غير مستهدفة",
        wrong_currency: "عملة السعر لا تطابق عملة العميل",
        invalid_price: "السعر ناقص أو غير صالح",
        wrong_page: "نوع الصفحة الحالية غير مستهدف",
        wrong_device: "الجهاز الحالي غير مستهدف",
        invalid_schedule: "التاريخ ناقص أو غير صالح",
      };
      diagnostic.textContent = `حالة المعاينة: ${labels[state] || state}`;
    }

    if (action) action.hidden = liveState !== "active";
    if (countdown) countdown.hidden = liveState !== "active" || campaign.dataset.countdownEnabled !== "true";

    if (liveState === "active" && campaign.dataset.countdownEnabled === "true") {
      this.updateCountdown(campaign);
      this.startTimer(campaign);
    }

    if (!editorPreview && !this.impressedCampaigns.has(campaign.dataset.campaignId)) {
      this.impressedCampaigns.add(campaign.dataset.campaignId);
      emitBarEvent("bar_impression", this.eventDetail(campaign));
    }
  }

  hideAll() {
    this.stopTimer();
    this.activeCampaign = null;
    this.hidden = true;
    this.section?.setAttribute("data-fomo-active", "false");
    this.querySelectorAll("[data-fomo-campaign]").forEach((campaign) => {
      campaign.hidden = true;
    });
  }

  handleVisibility() {
    if (document.visibilityState === "hidden") {
      this.stopTimer();
    } else if (this.activeCampaign) {
      this.evaluate();
    }
  }

  startTimer(campaign) {
    if (document.visibilityState === "hidden") return;
    const delay = campaign.dataset.showSeconds === "true" ? 1000 : 30000;
    this.intervalId = window.setInterval(() => this.updateCountdown(campaign), delay);
  }

  stopTimer() {
    if (this.intervalId) window.clearInterval(this.intervalId);
    this.intervalId = null;
  }

  updateCountdown(campaign) {
    const end = parseScheduledDate(campaign.dataset.endDate, campaign.dataset.endTime, campaign.dataset.timezone);
    if (!Number.isFinite(end)) {
      this.evaluate();
      return;
    }

    const remaining = end - Date.now();
    if (remaining <= 0) {
      emitBarEvent("bar_expired", this.eventDetail(campaign));
      this.evaluate();
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = campaign.dataset.showDays === "true" ? Math.floor((totalSeconds % 86400) / 3600) : Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const values = { days, hours, minutes, seconds };
    Object.entries(values).forEach(([unit, value]) => {
      const element = campaign.querySelector(`[data-fomo-value="${unit}"]`);
      if (element) element.textContent = String(value).padStart(2, "0");
    });
  }

  eventDetail(campaign) {
    return {
      bar_type: "fomo",
      section_id: this.dataset.sectionId,
      campaign_id: campaign?.dataset.campaignId || null,
      campaign_name: campaign?.dataset.campaignName || null,
      country: this.country,
      currency: this.currency,
      language: this.dataset.language,
      page_type: this.pageType,
    };
  }
}

class AfghaniAnnouncementShell extends HTMLElementBase {
  connectedCallback() {
    this.abortController = new AbortController();
    this.storageKey = `afghani-announcement-dismissed:${this.dataset.sectionId}`;

    if (!window.Shopify?.designMode) {
      const dismissedUntil = Number(safeStorageGet(this.storageKey));
      if (Number.isFinite(dismissedUntil) && dismissedUntil > Date.now()) {
        this.hidden = true;
        return;
      }
    }

    const carousel = this.querySelector("announcement-bar");
    if (carousel && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      carousel.removeAttribute("autoplay");
      carousel.player?.stop();
    }

    this.querySelector("[data-announcement-dismiss]")?.addEventListener(
      "click",
      () => {
        const hours = Number(this.dataset.dismissHours || 24);
        safeStorageSet(this.storageKey, String(Date.now() + hours * 60 * 60 * 1000));
        emitBarEvent("bar_dismiss", this.eventDetail());
        this.hidden = true;
      },
      { signal: this.abortController.signal },
    );

    this.querySelector("[data-announcement-pause]")?.addEventListener(
      "click",
      (event) => {
        const button = event.currentTarget;
        const paused = button.getAttribute("aria-pressed") === "true";
        button.setAttribute("aria-pressed", String(!paused));
        button.setAttribute("aria-label", paused ? button.dataset.pauseLabel : button.dataset.playLabel);
        this.classList.toggle("is-paused", !paused);

        if (carousel?.player) {
          if (paused) carousel.player.start();
          else carousel.player.stop();
        }
      },
      { signal: this.abortController.signal },
    );

    bindScrollActions(this, this.abortController.signal, () => this.eventDetail());

    this.querySelectorAll("a:not([data-bar-scroll-target])").forEach((link) => {
      link.addEventListener(
        "click",
        () => emitBarEvent("bar_click", { ...this.eventDetail(), destination_type: "link" }),
        { signal: this.abortController.signal },
      );
    });

    emitBarEvent("bar_impression", this.eventDetail());
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  eventDetail() {
    return {
      bar_type: "announcement",
      section_id: this.dataset.sectionId,
      country: (this.dataset.country || "").toUpperCase(),
      currency: (this.dataset.currency || "").toUpperCase(),
      language: this.dataset.language,
      page_type: this.dataset.pageType,
    };
  }
}

if (globalThis.customElements && !globalThis.customElements.get("afghani-fomo-bar")) {
  globalThis.customElements.define("afghani-fomo-bar", AfghaniFomoBar);
}

if (globalThis.customElements && !globalThis.customElements.get("afghani-announcement-shell")) {
  globalThis.customElements.define("afghani-announcement-shell", AfghaniAnnouncementShell);
}

export { normalizedList, parseScheduledDate, resolveCampaignState };
